import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import Mux from "../../../../prop-tracker-utils/mux.js";
import { Mapper, ValueMapper } from "../../../../prop-tracker-utils/mapper.js";
import Zipper from "../../../../prop-tracker-utils/zipper.js";

import { Root } from "./parent-assigner.js";

class ParentTrackerAssigner {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, parentAssigner) {
        this._parentAssigner = parentAssigner;

        this._loaded = shlWrapCtx.getWrapper({});
        this._loadedEvents = shlEvtCtx.getEvents(this._loaded);

        this._objectTracker = new PropTracker();
        this._objectDataTracker = new PropTracker();
        this._parentMapper = new Mapper(this._objectDataTracker, (key) => new ParentMapper(evtCtx, this._loaded, this._loadedEvents));

        this._zipper = new Zipper();
        this._zipper.setSource("child", this._objectTracker);
        this._zipper.setSource("parent", this._parentMapper);
    }

    trackData(objectData, objectDataEvents) {
        this._objectDataTracker.track(objectData, objectDataEvents);
    }

    trackObjects(objects, objectEvents) {
        if (objects) {
            this._loaded.objects = objects;
            this._loaded.objectEvents = objectEvents;
        } else {
            delete this._loaded.objects;
            delete this._loaded.objectEvents;
        }

        this._objectTracker.track(objects, objectEvents);
    }

    update() {
        this._zipper.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ props }) => {
                    const { oldValue: oldChild, newValue: newChild } = props.get("child");
                    const { oldValue: oldParent, newValue: newParent } = props.get("parent");

                    if (oldParent && oldChild) {
                        this._parentAssigner.unlink(oldParent, oldChild);
                    }

                    if (newParent && newChild) {
                        this._parentAssigner.link(newParent, newChild);
                    }
                });
            }
        });
    }
}

class ParentMapper extends ValueMapper {
    constructor(evtCtx, loaded, loadedEvents) {
        super();

        this._evtCtx = evtCtx;
        this._loaded = loaded;
        this._loadedEvents = loadedEvents;

        this._loadedTracker = new PropTracker();
        this._dataSet = new ValueTracker();
        this._parentKeyTracker = new SinglePropTracker();
        this._parentMux = new Mux();
        this._parentTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._loadedTracker.listenNeedsUpdate(needsUpdate);
        this._dataSet.listenNeedsUpdate(needsUpdate);
        this._parentKeyTracker.listenNeedsUpdate(needsUpdate);
        this._parentMux.listenNeedsUpdate(needsUpdate);
    }

    setValue(data) {
        if (data) {
            this._loadedTracker.track(this._loaded, this._loadedEvents);
            this._dataSet.setValue(true);
            this._parentKeyTracker.track(data, this._evtCtx.getEvents(data), "parent");
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._loadedTracker.track();
        this._dataSet.deleteValue();
        this._parentKeyTracker.track();
    }

    protected_startUpdate() {
        this._loadedTracker.updateIfNeeded(({ props }) => {
            const { newValue: objects } = props.get("objects");
            const { newValue: objectEvents } = props.get("objectEvents");

            this._parentMux.setSource(objects, objects && objectEvents);
        });

        this._parentKeyTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._parentMux.setKey(typeof newValue === "string" ? newValue : undefined);
        });

        if (this._parentMux.needsUpdate || this._dataSet.needsUpdate) {
            this._parentMux.update(({ prop: { newValue: parent } }) => {
                this._dataSet.update(({ prop: { newValue: dataSet } }) => {
                    if (dataSet) {
                        this._parentTracker.setValue(parent || Root);
                    } else {
                        this._parentTracker.deleteValue();
                    }
                });
            });
        }

        return this._parentTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._parentTracker.finishUpdate();
    }
}

export default ParentTrackerAssigner;
